import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Target, X, AlertCircle } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';
import { isFilledNumber, missingRequiredMessage } from '../utils/requiredFields';
import Pagination, { usePagination } from './Pagination';
import StyledDropdown from './StyledDropdown';
import DeleteImpactModal from './DeleteImpactModal';

const ThresholdManagement = () => {
  const [thresholds, setThresholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingThreshold, setEditingThreshold] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    tlOverrideThreshold: '',
    glOverrideThreshold: '',
    status: 'Active',
  });

  // Helper functions for number formatting
  const formatNumber = (value) => {
    if (!value) return '';
    const num = parseFloat(value.toString().replace(/,/g, ''));
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('en-MY').format(num);
  };

  const parseFormattedNumber = (value) => {
    if (!value) return '';
    return value.toString().replace(/,/g, '');
  };

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${getApiUrl()}/api/campaign-thresholds`, { 
        headers: getAuthHeaders() 
      });
      const result = await response.json();

      if (result.success) {
        setThresholds(result.data);
      }
    } catch (err) {
      console.error('Fetch data error:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateThreshold = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const missing = missingRequiredMessage([
        { label: 'Threshold Name', value: formData.name },
        { label: 'TL Override Threshold', value: formData.tlOverrideThreshold, check: isFilledNumber },
        { label: 'GL Override Threshold', value: formData.glOverrideThreshold, check: isFilledNumber },
      ]);
      if (missing) {
        setError(missing);
        return;
      }

      const response = await fetch(`${getApiUrl()}/api/campaign-thresholds`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: formData.name,
          tlOverrideThreshold: parseFloat(parseFormattedNumber(formData.tlOverrideThreshold)),
          glOverrideThreshold: parseFloat(parseFormattedNumber(formData.glOverrideThreshold)),
          status: formData.status,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setFormData({
          name: '',
          tlOverrideThreshold: '',
          glOverrideThreshold: '',
          status: 'Active',
        });
        setShowCreateModal(false);
        fetchData();
      } else {
        setError(result.message || 'Failed to create threshold');
      }
    } catch (err) {
      console.error('Create threshold error:', err);
      setError('Unable to connect to server');
    }
  };

  const handleEditClick = (threshold) => {
    setEditingThreshold(threshold);
    setFormData({
      name: threshold.name,
      tlOverrideThreshold: threshold.tlOverrideThreshold.toString(),
      glOverrideThreshold: threshold.glOverrideThreshold.toString(),
      status: threshold.status,
    });
    setShowEditModal(true);
  };

  const handleUpdateThreshold = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const missing = missingRequiredMessage([
        { label: 'Threshold Name', value: formData.name },
        { label: 'TL Override Threshold', value: formData.tlOverrideThreshold, check: isFilledNumber },
        { label: 'GL Override Threshold', value: formData.glOverrideThreshold, check: isFilledNumber },
      ]);
      if (missing) {
        setError(missing);
        return;
      }

      const response = await fetch(`${getApiUrl()}/api/campaign-thresholds/${editingThreshold.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: formData.name,
          tlOverrideThreshold: parseFloat(parseFormattedNumber(formData.tlOverrideThreshold)),
          glOverrideThreshold: parseFloat(parseFormattedNumber(formData.glOverrideThreshold)),
          status: formData.status,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setShowEditModal(false);
        setEditingThreshold(null);
        setFormData({
          name: '',
          tlOverrideThreshold: '',
          glOverrideThreshold: '',
          status: 'Active',
        });
        fetchData();
      } else {
        setError(result.message || 'Failed to update threshold');
      }
    } catch (err) {
      console.error('Update threshold error:', err);
      setError('Unable to connect to server');
    }
  };

  const handleDeleteThreshold = async (id) => {
    try {
      setError('');
      const response = await fetch(`${getApiUrl()}/api/campaign-thresholds/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        setDeleteConfirm(null);
        fetchData();
      } else {
        setError(result.message || 'Failed to delete threshold');
        setDeleteConfirm(null);
      }
    } catch (err) {
      console.error('Delete threshold error:', err);
      setError('Unable to connect to server');
      setDeleteConfirm(null);
    }
  };

  const getStatusBadge = (status) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        status === 'Active' 
          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
      }`}>
        {status}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Pagination
  const {
    currentPage,
    paginatedItems: paginatedThresholds,
    totalItems,
    handlePageChange,
  } = usePagination(thresholds, 10);

  const renderModal = (isEdit = false) => {
    const modalTitle = isEdit ? 'Edit Threshold' : 'Create New Threshold';
    const submitLabel = isEdit ? 'Update Threshold' : 'Create Threshold';
    const onSubmit = isEdit ? handleUpdateThreshold : handleCreateThreshold;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="neo-modal p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">{modalTitle}</h3>
            <button
              onClick={() => {
                if (isEdit) {
                  setShowEditModal(false);
                  setEditingThreshold(null);
                } else {
                  setShowCreateModal(false);
                }
                setFormData({
                  name: '',
                  tlOverrideThreshold: '',
                  glOverrideThreshold: '',
                  status: 'Active',
                });
                setError('');
              }}
              className="text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={onSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Threshold Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 neo-field dark:text-dark-text focus:outline-none"
                  placeholder="e.g., Q4 2025 Threshold"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  TL Override Threshold (RM) *
                </label>
                <input
                  type="text"
                  value={formatNumber(formData.tlOverrideThreshold)}
                  onChange={(e) => {
                    const rawValue = parseFormattedNumber(e.target.value);
                    if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                      setFormData({...formData, tlOverrideThreshold: rawValue});
                    }
                  }}
                  className="w-full px-3 py-2 neo-field dark:text-dark-text focus:outline-none"
                  placeholder="e.g., 2,000,000"
                  required
                />
                <p className="text-xs text-gray-500 dark:text-dark-muted mt-1">
                  Minimum sales required for Team Leader override
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  GL Override Threshold (RM) *
                </label>
                <input
                  type="text"
                  value={formatNumber(formData.glOverrideThreshold)}
                  onChange={(e) => {
                    const rawValue = parseFormattedNumber(e.target.value);
                    if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                      setFormData({...formData, glOverrideThreshold: rawValue});
                    }
                  }}
                  className="w-full px-3 py-2 neo-field dark:text-dark-text focus:outline-none"
                  placeholder="e.g., 5,000,000"
                  required
                />
                <p className="text-xs text-gray-500 dark:text-dark-muted mt-1">
                  Minimum sales required for Group Leader override
                </p>
              </div>

              {isEdit && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                    Status
                  </label>
                  <StyledDropdown
                    options={[
                      { value: 'Active', label: 'Active' },
                      { value: 'Inactive', label: 'Inactive' },
                    ]}
                    value={formData.status}
                    onChange={(val) => setFormData({ ...formData, status: val })}
                    placeholder="Select status"
                    className="w-full"
                  />
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  if (isEdit) {
                    setShowEditModal(false);
                    setEditingThreshold(null);
                  } else {
                    setShowCreateModal(false);
                  }
                  setFormData({
                    name: '',
                    tlOverrideThreshold: '',
                    glOverrideThreshold: '',
                    status: 'Active',
                  });
                  setError('');
                }}
                className="px-4 py-2 neo-btn text-gray-700 dark:text-dark-text"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 neo-btn-primary"
              >
                {submitLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 dark:text-primary-400 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-600 dark:text-dark-muted">Loading thresholds...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Error Message */}
      {error && !showCreateModal && !showEditModal && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Action Button */}
      <div className="flex items-center justify-end mb-6">
        <button
          onClick={() => {
            setShowCreateModal(true);
            setFormData({
              name: '',
              tlOverrideThreshold: '',
              glOverrideThreshold: '',
              status: 'Active',
            });
            setError('');
          }}
          className="neo-btn-primary px-4 py-2 text-sm font-medium flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Create Threshold</span>
        </button>
      </div>

      {/* Thresholds Table */}
      <div className="neo-raised overflow-hidden">
        <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200 dark:border-dark-border">
          <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text">All Thresholds</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
            <thead className="bg-gray-50 dark:bg-slate-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Threshold Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  TL Override Threshold
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  GL Override Threshold
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Products Using
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200/70 dark:divide-dark-border">
              {paginatedThresholds.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500 dark:text-dark-muted">
                    No thresholds found. Create your first threshold to get started.
                  </td>
                </tr>
              ) : (
                paginatedThresholds.map((threshold) => (
                  <tr key={threshold.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center mr-3">
                          <Target className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                        </div>
                        <div className="text-sm font-medium text-gray-900 dark:text-dark-text">{threshold.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-dark-text">
                        {formatCurrency(threshold.tlOverrideThreshold)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-dark-text">
                        {formatCurrency(threshold.glOverrideThreshold)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-dark-text">
                        {threshold._count?.productCampaigns || 0} product(s)
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(threshold.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEditClick(threshold)}
                          className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(threshold.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={10}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Create Modal */}
      {showCreateModal && renderModal(false)}

      {/* Edit Modal */}
      {showEditModal && renderModal(true)}

      <DeleteImpactModal
        open={!!deleteConfirm}
        entityType="threshold"
        entityId={deleteConfirm}
        title="Delete threshold"
        onClose={() => setDeleteConfirm(null)}
        onDeleted={() => { setDeleteConfirm(null); fetchData(); }}
      />
    </div>
  );
};

export default ThresholdManagement;



